import type {
  CreateUserBody,
  IssueManualLicenseBody,
  TransferLicenseBody,
  UserAccessMutationResponse,
} from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  blockUser,
  cooldownDevice,
  createUser,
  getUser,
  impersonate,
  issueManualLicense,
  listUsers,
  revokeLicense,
  revokeSession,
  transferLicense,
  type UsersQuery,
  unblockUser,
} from '../lib/users-api.js';

/* Hooks TanStack Query do domínio usuários (ADR-0356). As mutações invalidam o
   detail para refletir a mudança (license/session/device). */

export function useUsersList(filters: UsersQuery) {
  return useInfiniteQuery({
    queryKey: ['users', 'list', filters],
    queryFn: ({ pageParam }) => listUsers({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/* Criação de conta comum pré-verificada (ADR-0710): invalida a lista (o novo item
   aparece). Espelha `useCreateCompany`. */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) => createUser(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}

/* `options.enabled` permite abrir a query só quando o consumidor precisa (ex.: painel de
   contexto do chat que carrega o usuário apenas na aba ativa). Default true — retrocompatível
   com os call sites que não passam options (ADR-0704). */
export function useUserDetail(id: string, options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: ['users', 'detail', id],
    queryFn: () => getUser(id),
    enabled: options?.enabled ?? true,
  });
}

function useInvalidateUser(id: string): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['users', 'detail', id] });
  };
}

function useChangeUserAccess(
  id: string,
  operation: (id: string, reason: string) => Promise<UserAccessMutationResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => operation(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}

export const useBlockUser = (id: string) => useChangeUserAccess(id, blockUser);
export const useUnblockUser = (id: string) => useChangeUserAccess(id, unblockUser);

export function useImpersonate(id: string) {
  return useMutation({ mutationFn: (reason: string) => impersonate(id, reason) });
}

export function useRevokeLicense(userId: string) {
  const invalidate = useInvalidateUser(userId);
  return useMutation({
    mutationFn: (input: { readonly packId: string; readonly reason: string }) =>
      revokeLicense(userId, input.packId, input.reason),
    onSuccess: invalidate,
  });
}

export function useTransferLicense(userId: string) {
  const invalidate = useInvalidateUser(userId);
  return useMutation({
    mutationFn: (body: TransferLicenseBody) => transferLicense(userId, body),
    onSuccess: invalidate,
  });
}

export function useIssueManualLicense(userId: string) {
  const invalidate = useInvalidateUser(userId);
  return useMutation({
    mutationFn: (body: IssueManualLicenseBody) => issueManualLicense(userId, body),
    onSuccess: invalidate,
  });
}

export function useCooldownDevice(userId: string) {
  const invalidate = useInvalidateUser(userId);
  return useMutation({
    mutationFn: (input: {
      readonly deviceId: string;
      readonly durationHours: number;
      readonly reason: string;
    }) => cooldownDevice(input.deviceId, input.durationHours, input.reason),
    onSuccess: invalidate,
  });
}

export function useRevokeSession(userId: string) {
  const invalidate = useInvalidateUser(userId);
  return useMutation({
    mutationFn: (input: { readonly sessionId: string; readonly reason: string }) =>
      revokeSession(userId, input.sessionId, input.reason),
    onSuccess: invalidate,
  });
}
